import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Button, Title } from "lola-framework-ui-test";
import { FloatingField, type FloatingFieldColors } from "../FloatingField";
import { useStore } from "../../store/globalState";
import {
  PayoutMethodType,
  type EstimateOption,
  type PayoutInput,
} from "../../interfaces/payout";
import type { RecipientDestination } from "../../api/recipients";
import { mockCatalogFor } from "../../gallery/sendMock";
import {
  compatibleBookDestinations,
  destinationListKey,
  type CompatibleBookDestination,
} from "../../utils/recipient-destinations";
import {
  bankPrefixMismatch,
  inspectBankAccountNumber,
} from "../../utils/bankAccountNumber";
import {
  CARDHOLDER_NAME_INPUT_MAX_LENGTH,
  formatCardholderNameInput,
  isValidCardholderName,
} from "../../utils/cardholderName";
import {
  catalogNamedPayer,
  dedupeOfferableCashPayers,
  dedupeOfferableCashPayersForState,
  payerCodesEquivalent,
  resolveCashPickupOption,
} from "../../utils/payerCode";
import { BackArrowLight, CloseLight } from "../../assets/navIcons";

const MONO_INPUT: CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  letterSpacing: "0.04em",
};

function payoutFieldColors(view: {
  background: string;
  title: string;
  bodyCopy: string;
  subtitile: string;
  errorColor?: string;
}): FloatingFieldColors {
  return {
    background: view.background,
    title: view.title,
    bodyCopy: view.bodyCopy,
    inactiveColor: view.subtitile,
    errorColor: view.errorColor,
  };
}

export interface PayoutDetailsResult {
  payout: PayoutInput;
  /** Pre-quote option the selection priced against, when one matched. */
  option: EstimateOption | null;
}

export interface PayoutDetailsSlotProps {
  method: PayoutMethodType;
  destinationCountry: string;
  /** Pre-quote options for the corridor — drives the priced SKU. */
  options: EstimateOption[];
  /** Habitual destinations from a recipient-book pick (may be empty). */
  bookDestinations?: RecipientDestination[];
  /** Existing payout to seed the form when re-editing. */
  value?: PayoutInput | null;
  /** Control Dock: 3-row saved-list shimmer. */
  habitsBusy?: boolean;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (result: PayoutDetailsResult) => void;
}

/** Local account length expected per destination country (digits only). */
const ACCOUNT_LENGTH: Record<string, number> = { MX: 18, AR: 22, PE: 20 };
const MAX_ACCOUNT_NUMBER_LENGTH = 34;

const payerCodeOf = (
  method: PayoutMethodType,
  payout: PayoutInput,
): string | null => {
  switch (method) {
    case PayoutMethodType.BANK_ACCOUNT:
      return payout.bankId ?? null;
    case PayoutMethodType.CASH_PICKUP:
      return payout.payerId ?? null;
    case PayoutMethodType.MOBILE_WALLET:
      return payout.carrierId ?? null;
    default:
      return null;
  }
};

const resolveOption = (
  method: PayoutMethodType,
  payout: PayoutInput,
  options: EstimateOption[],
): EstimateOption | null => {
  const forMethod = options.filter((o) => o.payoutMethod === method);
  const payerCode = payerCodeOf(method, payout);
  if (method === PayoutMethodType.CASH_PICKUP) {
    if (!payerCode) return null;
    return resolveCashPickupOption(options, payerCode, payout.stateCode);
  }
  const byPayer = payerCode
    ? forMethod.find((o) => o.payerCode === payerCode)
    : undefined;
  return byPayer ?? forMethod[0] ?? null;
};

/**
 * Gallery-safe second stage of the payout picker. Mirrors the production
 * `PayoutDetailsSlot` UI (bank / cash / card / wallet sub-forms) but sources its
 * bank / state / payer / carrier lists from the static `mockCatalogFor` data
 * instead of the live `/widget/catalog/*` endpoints.
 */
export const PayoutDetailsSlot: React.FC<PayoutDetailsSlotProps> = ({
  method,
  destinationCountry,
  options,
  bookDestinations = [],
  value,
  habitsBusy = false,
  onBack,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation("send");
  const { theme } = useStore();
  const { bodyCopy } = theme.whiteView;

  const savedEntries = useMemo(
    () => compatibleBookDestinations(bookDestinations, method, options),
    [bookDestinations, method, options],
  );

  const submit = (payout: PayoutInput): void => {
    onSubmit({ payout, option: resolveOption(method, payout, options) });
  };

  const submitSaved = (entry: CompatibleBookDestination): void => {
    const option =
      options.find((o) => o.capabilityKey === entry.capabilityKey) ??
      resolveOption(method, entry.payout, options);
    onSubmit({ payout: entry.payout, option });
  };

  return (
    <section className="payout-details-slot">
      <header className="payout-details-header">
        <button
          type="button"
          className="payout-header-action payout-back-btn"
          aria-label={t("common.back")}
          onClick={onBack}
        >
          <BackArrowLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
        <Title
          title={t(`payout.details.${method}.title`)}
          align="center"
          color={bodyCopy}
        />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label="Close"
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>

      {(method === PayoutMethodType.CASH_PICKUP ||
        method === PayoutMethodType.MOBILE_WALLET) &&
        (habitsBusy || savedEntries.length > 0) && (
        <div className="payout-form">
          <SavedDestinationsPicker
            entries={savedEntries}
            method={method}
            destinationCountry={destinationCountry}
            habitsBusy={habitsBusy}
            onSelect={submitSaved}
          />
        </div>
      )}

      {method === PayoutMethodType.BANK_ACCOUNT && (
        <BankAccountForm
          destinationCountry={destinationCountry}
          value={value}
          onSubmit={submit}
        />
      )}
      {method === PayoutMethodType.CASH_PICKUP && (
        <CashPickupForm
          destinationCountry={destinationCountry}
          options={options}
          value={value}
          onSubmit={submit}
        />
      )}
      {method === PayoutMethodType.PUSH_TO_CARD && (
        <CardForm value={value} onSubmit={submit} />
      )}
      {method === PayoutMethodType.MOBILE_WALLET && (
        <MobileWalletForm
          destinationCountry={destinationCountry}
          value={value}
          onSubmit={submit}
        />
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Saved book destinations
// ---------------------------------------------------------------------------

interface SavedDestinationsPickerProps {
  entries: CompatibleBookDestination[];
  method: PayoutMethodType;
  destinationCountry: string;
  habitsBusy?: boolean;
  onSelect: (entry: CompatibleBookDestination) => void;
}

const SavedListSkeleton = () => (
  <>
    {Array.from({ length: 2 }, (_, i) => (
      <div key={i} className="payout-saved-skeleton" aria-hidden="true">
        <span className="payout-saved-skeleton-line" />
        <span className="payout-saved-skeleton-line short" />
      </div>
    ))}
  </>
);

const SavedDestinationsPicker: React.FC<SavedDestinationsPickerProps> = ({
  entries,
  method,
  destinationCountry,
  habitsBusy = false,
  onSelect,
}) => {
  const { t } = useTranslation("send");
  const catalog = mockCatalogFor(destinationCountry);

  const labeled = entries
    .map((entry) => {
      const { payout } = entry;
      if (method === PayoutMethodType.CASH_PICKUP) {
        const stateLabel =
          catalog.cashStates.find(
            (s) =>
              s.code.toUpperCase() === (payout.stateCode ?? "").toUpperCase(),
          )?.name ?? payout.stateCode;
        const payerLabel = catalogNamedPayer(
          payout.payerId,
          catalog.cashPayers,
        );
        if (!payerLabel || !stateLabel) return null;
        return { entry, title: payerLabel, subtitle: stateLabel };
      }
      if (method === PayoutMethodType.MOBILE_WALLET) {
        return {
          entry,
          title: payout.phone || t("payout.methods.MOBILE_WALLET"),
          subtitle:
            catalogNamedPayer(payout.carrierId, catalog.carriers) || undefined,
        };
      }
      return null;
    })
    .filter((row): row is NonNullable<typeof row> => row != null);

  const showBlock = habitsBusy || labeled.length > 0;
  if (!showBlock) return null;

  return (
    <section
      className="payout-saved"
      aria-label={t("payout.savedDestinations.aria")}
      aria-busy={habitsBusy}
    >
      <span className="payout-field-label">
        {t("payout.savedDestinations.label")}
      </span>
      <div role="list" className="payout-saved-list">
        {habitsBusy ? (
          <SavedListSkeleton />
        ) : (
          labeled.map(({ entry, title, subtitle }) => (
            <button
              key={destinationListKey(entry.destination)}
              type="button"
              role="listitem"
              className="payout-saved-item"
              onClick={() => onSelect(entry)}
            >
              <span className="payout-saved-title">{title}</span>
              {subtitle && <span className="payout-hint">{subtitle}</span>}
            </button>
          ))
        )}
      </div>
      <span className="payout-hint payout-or-new">
        {t("payout.savedDestinations.orNew")}
      </span>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Sub-forms
// ---------------------------------------------------------------------------

const sanitizeDigits = (raw: string, length?: number): string => {
  const digits = raw.replace(/\D/g, "");
  return length != null ? digits.slice(0, length) : digits;
};

const sanitizeAccountNumber = (raw: string, exactLength?: number): string => {
  if (exactLength != null) return sanitizeDigits(raw, exactLength);
  return raw
    .replace(/\s/g, "")
    .replace(/[^a-z0-9]/gi, "")
    .toUpperCase()
    .slice(0, MAX_ACCOUNT_NUMBER_LENGTH);
};

const groupDigits = (digits: string): string =>
  digits.replace(/(\d{4})(?=\d)/g, "$1 ");

interface SubFormProps {
  destinationCountry: string;
  value?: PayoutInput | null;
  onSubmit: (payout: PayoutInput) => void;
}

const BankAccountForm: React.FC<SubFormProps> = ({
  destinationCountry,
  value,
  onSubmit,
}) => {
  const { t } = useTranslation("send");
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy, errorColor } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const banks = mockCatalogFor(destinationCountry).banks;

  const [bankId, setBankId] = useState<string | null>(value?.bankId ?? null);
  const [accountNumber, setAccountNumber] = useState<string>(
    value?.accountNumber ?? "",
  );
  const expectedLength = ACCOUNT_LENGTH[destinationCountry.toUpperCase()];
  const identifier = sanitizeAccountNumber(accountNumber, expectedLength);
  const inspection = inspectBankAccountNumber(destinationCountry, identifier);
  const selectedPrefixes = banks.find((b) => b.id === bankId)?.accountPrefixes;
  const mismatch =
    inspection.canContinue &&
    bankPrefixMismatch(inspection.bankCode, selectedPrefixes);
  const valid = Boolean(bankId) && inspection.canContinue;

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !bankId) return;
        onSubmit({ bankId, accountNumber: identifier });
      }}
    >
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.bank")}
          value={bankId ?? ""}
          colors={fieldColors}
        >
          <select
            style={{ color: bodyCopy, cursor: "pointer" }}
            value={bankId ?? ""}
            onChange={(e) => {
              setBankId(e.target.value || null);
            }}
          >
            <option value="" />
            {banks.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </FloatingField>
        {banks.length === 0 && (
          <span className="payout-hint">{t("payout.fields.noBanks")}</span>
        )}
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.accountNumber")}
          value={identifier}
          colors={fieldColors}
        >
          <input
            type="text"
            inputMode={expectedLength == null ? "text" : "numeric"}
            maxLength={expectedLength ?? MAX_ACCOUNT_NUMBER_LENGTH}
            autoComplete="off"
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={identifier}
            onChange={(e) => {
              setAccountNumber(
                sanitizeAccountNumber(e.target.value, expectedLength),
              );
            }}
          />
        </FloatingField>
        {expectedLength != null && (
          <span className="payout-hint">
            {identifier.length} / {expectedLength}
          </span>
        )}
        {identifier.length > 0 && !inspection.formatOk && (
          <span className="payout-hint" style={{ color: errorColor }}>
            {t("payout.fields.accountNumberInvalid")}
          </span>
        )}
        {mismatch && (
          <span className="payout-hint" style={{ color: errorColor }}>
            {t("payout.fields.bankMismatch")}
          </span>
        )}
      </div>

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {mismatch
          ? t("payout.fields.bankMismatchConfirm")
          : t("common.continue")}
      </Button>
    </form>
  );
};

interface CashPickupFormProps extends SubFormProps {
  options: EstimateOption[];
}

const CashPickupForm: React.FC<CashPickupFormProps> = ({
  destinationCountry,
  options,
  value,
  onSubmit,
}) => {
  const { t } = useTranslation("send");
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const catalog = mockCatalogFor(destinationCountry);

  const offerablePayerCodes = useMemo(
    () => new Set(dedupeOfferableCashPayers(options)),
    [options],
  );

  const [stateCode, setStateCode] = useState<string | null>(
    value?.stateCode ?? null,
  );
  const [payerId, setPayerId] = useState<string | null>(value?.payerId ?? null);

  const offerablePayerCodesForState = useMemo(
    () => new Set(dedupeOfferableCashPayersForState(options, stateCode)),
    [options, stateCode],
  );

  const stateOptions = useMemo(() => {
    const all = catalog.cashStates;
    if (offerablePayerCodes.size === 0) return all;
    return all.filter(
      (s) => dedupeOfferableCashPayersForState(options, s.code).length > 0,
    );
  }, [catalog.cashStates, offerablePayerCodes.size, options]);

  useEffect(() => {
    if (!stateCode) return;
    if (stateOptions.length === 0) return;
    const stillOffered = stateOptions.some(
      (s) => s.code.toUpperCase() === stateCode.toUpperCase(),
    );
    if (!stillOffered) {
      setStateCode(null);
      setPayerId(null);
    }
  }, [stateOptions, stateCode]);

  const payerList = useMemo(() => {
    if (!stateCode) return [];
    const fromQuote = [...offerablePayerCodesForState];
    if (fromQuote.length > 0) {
      return fromQuote
        .map((code) => {
          const name = catalogNamedPayer(code, catalog.cashPayers);
          return name ? { id: code, name } : null;
        })
        .filter((row): row is { id: string; name: string } => row != null)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    if (offerablePayerCodes.size === 0) {
      return catalog.cashPayers.filter((p) => p.name?.trim());
    }
    return catalog.cashPayers.filter(
      (p) =>
        Boolean(p.name?.trim()) &&
        [...offerablePayerCodes].some((code) =>
          payerCodesEquivalent(code, p.id),
        ),
    );
  }, [
    stateCode,
    offerablePayerCodesForState,
    catalog.cashPayers,
    offerablePayerCodes,
  ]);

  const effectivePayerId = useMemo(() => {
    if (!payerId) return null;
    const exact = payerList.find((p) => p.id === payerId);
    if (exact) return exact.id;
    const aliased = payerList.find((p) =>
      payerCodesEquivalent(p.id, payerId),
    );
    return aliased?.id ?? null;
  }, [payerId, payerList]);

  const valid = Boolean(
    stateCode &&
      effectivePayerId &&
      resolveCashPickupOption(options, effectivePayerId, stateCode),
  );

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !stateCode || !effectivePayerId) return;
        onSubmit({ stateCode, payerId: effectivePayerId });
      }}
    >
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.state")}
          value={stateCode ?? ""}
          colors={fieldColors}
        >
          <select
            style={{ color: bodyCopy, cursor: "pointer" }}
            value={stateCode ?? ""}
            onChange={(e) => {
              setStateCode(e.target.value || null);
              setPayerId(null);
            }}
          >
            <option value="" />
            {stateOptions.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </FloatingField>
        {stateOptions.length === 0 && (
          <span className="payout-hint">{t("payout.fields.noStates")}</span>
        )}
      </div>

      {stateCode && (
        <div className="payout-field">
          <FloatingField
            label={t("payout.fields.payer")}
            value={effectivePayerId ?? ""}
            colors={fieldColors}
          >
            <select
              style={{ color: bodyCopy, cursor: "pointer" }}
              value={effectivePayerId ?? ""}
              onChange={(e) => setPayerId(e.target.value || null)}
            >
              <option value="" />
              {payerList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </FloatingField>
          {payerList.length === 0 && (
            <span className="payout-hint">{t("payout.fields.noPayers")}</span>
          )}
        </div>
      )}

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {t("common.continue")}
      </Button>
    </form>
  );
};

const formatCardExpiry = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const isExpiryValid = (expiry: string): boolean => {
  const m = expiry.match(/^(\d{2})\/(\d{2})$/);
  if (!m) return false;
  const month = Number(m[1]);
  return month >= 1 && month <= 12;
};

interface CardFormProps {
  value?: PayoutInput | null;
  onSubmit: (payout: PayoutInput) => void;
}

const CardForm: React.FC<CardFormProps> = ({ value, onSubmit }) => {
  const { t } = useTranslation("send");
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const [pan, setPan] = useState(value?.cardPan ?? "");
  const [expiry, setExpiry] = useState(value?.cardExpiry ?? "");
  const [cardholderName, setCardholderName] = useState(
    formatCardholderNameInput(value?.cardholderName ?? ""),
  );

  const digits = pan.replace(/\D/g, "");
  const valid =
    digits.length >= 13 &&
    isExpiryValid(expiry) &&
    isValidCardholderName(cardholderName);

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSubmit({
          cardPan: digits,
          cardExpiry: expiry,
          cardholderName: cardholderName.trim(),
          last4: digits.slice(-4),
        });
      }}
    >
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.cardholderName")}
          value={cardholderName}
          required
          colors={fieldColors}
        >
          <input
            type="text"
            style={{ color: bodyCopy }}
            value={cardholderName}
            maxLength={CARDHOLDER_NAME_INPUT_MAX_LENGTH}
            autoComplete="cc-name"
            autoCapitalize="characters"
            spellCheck={false}
            onChange={(e) =>
              setCardholderName(formatCardholderNameInput(e.target.value))
            }
          />
        </FloatingField>
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.cardPan")}
          placeholder={t("payout.fields.cardPanPlaceholder")}
          value={groupDigits(digits)}
          required
          colors={fieldColors}
        >
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-number"
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={groupDigits(digits)}
            onChange={(e) => setPan(sanitizeDigits(e.target.value, 19))}
          />
        </FloatingField>
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.cardExpiry")}
          placeholder={t("payout.fields.cardExpiryPlaceholder")}
          value={expiry}
          required
          colors={fieldColors}
        >
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-exp"
            maxLength={5}
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={expiry}
            onChange={(e) => setExpiry(formatCardExpiry(e.target.value))}
          />
        </FloatingField>
      </div>

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {t("common.continue")}
      </Button>
    </form>
  );
};

const MobileWalletForm: React.FC<SubFormProps> = ({
  destinationCountry,
  value,
  onSubmit,
}) => {
  const { t } = useTranslation("send");
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const carriers = mockCatalogFor(destinationCountry).carriers;

  const [carrierId, setCarrierId] = useState<string | null>(
    value?.carrierId ?? null,
  );
  const [phone, setPhone] = useState(value?.phone ?? "");

  const valid = Boolean(carrierId) && phone.replace(/\D/g, "").length >= 8;

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !carrierId) return;
        onSubmit({ carrierId, phone });
      }}
    >
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.carrier")}
          value={carrierId ?? ""}
          colors={fieldColors}
        >
          <select
            style={{ color: bodyCopy, cursor: "pointer" }}
            value={carrierId ?? ""}
            onChange={(e) => setCarrierId(e.target.value || null)}
          >
            <option value="" />
            {carriers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FloatingField>
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.phone")}
          value={phone}
          colors={fieldColors}
        >
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </FloatingField>
      </div>

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {t("common.continue")}
      </Button>
    </form>
  );
};
